import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

export const openApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL
});

// ============================================================
// Interceptors registrados UMA ÚNICA VEZ no nível do módulo
// Nunca acumulam, nunca têm race condition com React
// ============================================================

// Callback que o useAuth pode setar para reagir a 401
let onUnauthorized = null;
export const setOnUnauthorized = (cb) => { onUnauthorized = cb; };

const debugAuthError = (error, source) => {
  if (process.env.NODE_ENV !== "development") return;

  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return;

  const method = (error?.config?.method || "get").toUpperCase();
  const url = error?.config?.url || "unknown-url";
  console.debug(`[AUTH_DEBUG] ${source} -> ${status} ${method} ${url}`);
};

// REQUEST: injeta token do localStorage em toda requisição
api.interceptors.request.use(
  (config) => {
    const isRefreshRequest = config.url?.includes("/auth/refresh_token");
    const isMessagePost = (config.method || "get").toLowerCase() === "post" && /^\/messages\/[^/]+\/?$/.test(config.url || "");
    const hasFormData = typeof FormData !== "undefined" && config.data instanceof FormData;
    if ((config.timeout == null || config.timeout === 0) && (isRefreshRequest || (isMessagePost && !hasFormData))) {
      config.timeout = 30000;
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers ?? {};
      try {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
      } catch {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// One refresh promise is shared by API requests and the socket worker.
let refreshPromise = null;

export const refreshSession = ({ includeUser = false } = {}) => {
  if (!refreshPromise) {
    refreshPromise = api.post("/auth/refresh_token", undefined, { _skipAuthRefresh: true })
      .then(({ data }) => {
        if (!data?.token) {
          const error = new Error("Refresh response did not contain a token");
          error.code = "ERR_INVALID_REFRESH_RESPONSE";
          throw error;
        }
        localStorage.setItem("token", JSON.stringify(data.token));
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        return data;
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          debugAuthError(error, "refresh-attempt-failed");
          clearSession();
        }
        throw error;
      })
      .finally(() => { refreshPromise = null; });
  }

  return refreshPromise.then(data => includeUser ? data : data.token);
};

const clearSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("sessionId");
  api.defaults.headers.Authorization = undefined;
  if (onUnauthorized) onUnauthorized();
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    // Bloqueio por PLANO (nao e auth): sem refresh, sem toast — vai para pagina de indisponivel.
    if (status === 403 && error?.response?.data?.error === "ERR_FEATURE_NOT_IN_PLAN") {
      if (window.location.pathname !== "/recurso-indisponivel") {
        window.location.replace("/recurso-indisponivel");
      }
      return new Promise(() => {});
    }

    // Never refresh the refresh endpoint or requests that opted out.
    if (originalRequest?._skipAuthRefresh || originalRequest?.url?.includes("/auth/refresh_token")) {
      return Promise.reject(error);
    }

    // Não tentar refresh para login; queremos mostrar o erro real do login (ex: fora do expediente)
    if (originalRequest?.url?.includes("/auth/login")) {
      return Promise.reject(error);
    }

    // Only authentication failures trigger token renewal. A permission 403
    // must remain a permission error (feature-plan handling is above).
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const token = await refreshSession();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshErr) {
        debugAuthError(error, "original-request-unauthorized");
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
